import React from 'react';
import { useDispatch } from 'react-redux';
import type { QueryKey } from '@tanstack/react-query';
import classNames from 'classnames';
import { Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ApprovalWorkflowItemDto, AWAssetDto } from '@api/Api';
import {
  useDeleteApprovalWorkflow,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useOrganization } from '@components/OrganizationBoundary';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  DeleteIcon,
  CrossIcon,
  DiamondUpgradeIcon,
  ScissorsIcon
} from '@icons';

import './ApprovalBulkActions.less';

type ApprovalBulkManagementProps = {
  assets: AWAssetDto[];
  approvalWorkflow: ApprovalWorkflowItemDto;
  queryKey?: QueryKey;
  onClose: () => void;
};

export default function ApprovalBulkManagement({
  assets,
  approvalWorkflow,
  queryKey,
  onClose
}: ApprovalBulkManagementProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { currentOrganization } = useOrganization();
  const approvalWorkflowAssetsCount = approvalWorkflow?.assets?.length;
  const approvalWorkflowId = approvalWorkflow.id;
  const canManage = !!approvalWorkflow?.permissions?.manage;
  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;

  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({
    queryKey
  });

  const {
    mutateAsync: deleteApprovalWorkflow,
    isPending: isLoadingDeleteApprovalWorkflow
  } = useDeleteApprovalWorkflow({});

  const isLoadingDelete =
    isLoadingUpdateApprovalWorkflow || isLoadingDeleteApprovalWorkflow;

  const onDelete = () => {
    if (isLoadingUpdateApprovalWorkflow) return;
    if (!canManage) return;
    const isDeleteApproval = assets.length === approvalWorkflowAssetsCount;
    Modal.confirm({
      focusTriggerAfterClose: true,
      zIndex: 1010,
      width: 474,
      icon: null,
      centered: true,
      closable: true,
      maskClosable: true,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: { minWidth: 124, height: 48, padding: '8px 15px' }
      },
      cancelButtonProps: {
        style: {
          marginRight: '16px',
          border: 'none',
          height: 48,
          padding: '8px 32px'
        }
      },
      onOk: async () => {
        if (isDeleteApproval) {
          await deleteApprovalWorkflow({
            id: approvalWorkflow.id
          });
          navigate(
            `/campaigns/single/${approvalWorkflow.campaignId}?tab=approvals`
          );
          return;
        }
        await updateApprovalWorkflow({
          id: approvalWorkflowId,
          assets: {
            remove: assets.map((asset) => asset.id)
          }
        });
        onClose();
      },
      closeIcon: <CrossIcon />,
      content: (
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{ fontSize: 22, marginBottom: '0.5em' }}
          >
            Delete media
          </h2>
          {!isDeleteApproval && (
            <p
              className="modal_container__subtitle"
              style={{ marginBottom: '48px' }}
            >
              Are you sure you want to delete media?
            </p>
          )}
          {isDeleteApproval && (
            <p
              className="modal_container__subtitle"
              style={{ marginBottom: '48px' }}
            >
              You’re about to delete all media in{' '}
              <strong>{approvalWorkflow.name}</strong> approval routing. This
              will also remove the approval, including all its stages and data.
              Are you sure you want to proceed?
            </p>
          )}
        </div>
      )
    });
  };

  const onSplit = () => {
    if (!canManage) return;
    if (!awFeatureEnabled) return;
    dispatch(
      toggleApprovalModal({
        visible: true,
        permissions: {
          create: canCreateAw
        },
        campaignId: approvalWorkflow.campaignId,
        approvalWorkflowSplit: {
          ...approvalWorkflow,
          assets
        },
        callback: () => {
          onClose();
        }
      })
    );
  };

  return (
    <div
      className={classNames('approval-bulk-actions', {
        'approval-bulk-actions--active': !!assets?.length
      })}
    >
      <div className="approval-bulk-actions_top">
        <div className="approval-bulk-actions_info">
          <div className="approval-bulk-actions_info-icon" />
          <div className="approval-bulk-actions_info-text">
            {assets?.length} media selected
          </div>
        </div>
        <div className="approval-bulk-actions_close" onClick={onClose} />
      </div>
      <div className="approval-bulk-actions_actions">
        <Button
          type="text"
          size="small"
          loading={isLoadingDelete}
          disabled={!canManage}
          icon={<DeleteIcon fill="currentColor" />}
          onClick={onDelete}
        >
          Delete
        </Button>
        <UpgradePlanTooltip
          text=" to a paid plan to access this feature."
          placement="top"
          isCanVisible={!awFeatureEnabled}
        >
          <Button
            type="text"
            size="small"
            disabled={isLoadingDelete || !canManage || !awFeatureEnabled}
            icon={
              !awFeatureEnabled ? (
                <DiamondUpgradeIcon className="upgrade-icon" />
              ) : (
                <ScissorsIcon />
              )
            }
            onClick={onSplit}
          >
            Split into a new approval
          </Button>
        </UpgradePlanTooltip>
      </div>
    </div>
  );
}
